import { useEffect, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { Switch } from "../components/Switch";

import {
  fetchMobileClientFlags,
  MOBILE_FLAG_COPY,
  MOBILE_FLAG_KEYS,
  upsertAccountMobileFeatureFlags,
  type MobileFlagDraft,
} from "../data/operator-controls";
import { useReadModel } from "../data/useReadModel";

export function MobileFeatureFlagsPanel({
  client,
  accountId,
  canView = false,
  canManage = false,
  pollMs,
}: {
  client: SupabaseClient;
  accountId: string;
  canView?: boolean;
  canManage?: boolean;
  pollMs?: number;
}) {
  if (!canView) {
    return null;
  }
  return (
    <MobileFeatureFlagsPanelInner
      client={client}
      accountId={accountId}
      canManage={canManage}
      {...(pollMs !== undefined ? { pollMs } : {})}
    />
  );
}

function MobileFeatureFlagsPanelInner({
  client,
  accountId,
  canManage,
  pollMs,
}: {
  client: SupabaseClient;
  accountId: string;
  canManage: boolean;
  pollMs?: number;
}) {
  const [reloadNonce, setReloadNonce] = useState(0);
  const { status, data, error } = useReadModel<MobileFlagDraft>(
    () => fetchMobileClientFlags(client, accountId),
    [client, accountId, reloadNonce],
    pollMs !== undefined ? { pollMs } : {},
  );

  if (status === "loading" && data === null) {
    return <p className="surface__state" role="status">Loading mobile flags...</p>;
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load mobile feature flags."}
      </p>
    );
  }
  if (data === null) {
    return null;
  }

  return (
    <MobileFlagEditor
      client={client}
      accountId={accountId}
      flags={data}
      canManage={canManage}
      onSaved={() => setReloadNonce((nonce) => nonce + 1)}
    />
  );
}

function MobileFlagEditor({
  client,
  accountId,
  flags,
  canManage,
  onSaved,
}: {
  client: SupabaseClient;
  accountId: string;
  flags: MobileFlagDraft;
  canManage: boolean;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<MobileFlagDraft>(flags);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);

  useEffect(() => {
    setDraft(flags);
  }, [flags]);

  function setFlag(key: keyof MobileFlagDraft, value: boolean): void {
    setDraft((current) => ({ ...current, [key]: value }));
    setFailure(null);
    setReceipt(null);
  }

  async function submit(): Promise<void> {
    if (busy || !canManage) return;
    setBusy(true);
    setFailure(null);
    setReceipt(null);
    const outcome = await upsertAccountMobileFeatureFlags(client, accountId, draft);
    if (outcome.ok) {
      setReceipt("Mobile flags saved.");
      onSaved();
    } else {
      setFailure(outcome.reason);
    }
    setBusy(false);
  }

  return (
    <div className="operator-panel">
      <div className="mobile-flags__grid" aria-label="Mobile feature flags">
        {MOBILE_FLAG_KEYS.map((key) => (
          <article key={key} className={`mobile-flags__card ${draft[key] ? "mobile-flags__card--on" : ""}`}>
            <div className="mobile-flags__head">
              <span className="mobile-flags__title">{MOBILE_FLAG_COPY[key]}</span>
              <span className={`rollout__badge ${draft[key] ? "rollout__badge--ready" : "rollout__badge--disabled"}`}>
                {draft[key] ? "On" : "Off"}
              </span>
            </div>
            <Switch
              checked={draft[key]}
              disabled={!canManage || busy}
              layout="compact"
              label="Account default"
              onChange={(value) => setFlag(key, value)}
            />
          </article>
        ))}
      </div>
      {canManage ? (
        <div className="policy__editor-actions">
          <button type="button" className="minter__confirm-go" disabled={busy} onClick={() => void submit()}>
            {busy ? "Saving..." : "Save mobile flags"}
          </button>
        </div>
      ) : (
        <p className="page__note">Read-only for this role.</p>
      )}
      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">
          {failure}
        </p>
      ) : null}
      {receipt !== null ? (
        <p className="surface__state surface__state--ok" role="status">
          {receipt}
        </p>
      ) : null}
    </div>
  );
}
